import { Link } from 'react-router-dom';
import type { DocumentPageSchema } from '@avisoma/shared';
import { ArrowUpRightIcon, Square2StackIcon } from '@heroicons/react/24/outline';
import { LazyImage } from '../../components/LazyImage';
import { FullScreenImagePreviewTrigger } from '../../components/FullScreenImagePreview';
import { PagePreviewHoverProvider } from '../../components/pagePreview/usePagePreviewHover';
import { PagePreviewHoverCard } from '../../components/pagePreview/PagePreviewHoverCard';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';
import { SectionBandOverlay, sectionBandPct } from './SectionBandOverlay';

interface ParentPageCardProps {
  parentPageId: string;
  sectionBounds: DocumentPageSchema['sectionBounds'];
}

/**
 * Shown on a CHILD segment-page (one cropped from a multi-section parent during
 * section-aware reconstruction). Surfaces the parent's id, the section this child
 * was cut from, a thumbnail preview, and a link back to the parent page view.
 */
export function ParentPageCard({ parentPageId, sectionBounds }: ParentPageCardProps) {
  const href = `/document-pages/${parentPageId}`;
  const heading = sectionBounds?.heading?.trim();
  const sectionLabel =
    typeof sectionBounds?.order === 'number'
      ? `Section ${sectionBounds.order + 1}${heading ? `: “${heading}”` : ''}`
      : null;

  // pageWidth/pageHeight drive the preview's aspect ratio so the image fills the
  // box edge-to-edge and the overlaid band lines up exactly (see sectionBandPct).
  const band = sectionBandPct(sectionBounds);

  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6 border-b border-gray-200 dark:border-zinc-700">
        <div className="flex items-center gap-2">
          <Square2StackIcon className="size-5 text-gray-400 dark:text-zinc-400" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Segment of a larger page</h2>
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
          This page was cropped from a multi-section source page during reconstruction.
        </p>
      </div>
      <div className="flex items-start gap-4 p-6">
        <PagePreviewHoverProvider>
          <PagePreviewHoverCard
            page={{ id: parentPageId }}
            className="group relative w-40 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100 shadow-sm dark:border-zinc-700 dark:bg-zinc-700"
            style={{ aspectRatio: band ? band.aspectRatio : '3 / 4' }}
          >
            <Link to={href} aria-label="Open parent page" className="block h-full w-full transition hover:opacity-90">
              <LazyImage src={pagePreviewSrc(parentPageId, { resize: 'x400' })} className="h-full w-full" />
            </Link>
            <SectionBandOverlay sectionBounds={sectionBounds} label={sectionLabel} />
            <FullScreenImagePreviewTrigger pageId={parentPageId} />
          </PagePreviewHoverCard>
        </PagePreviewHoverProvider>
        <div className="min-w-0 flex-1">
          {sectionLabel && (
            <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">{sectionLabel}</p>
          )}
          <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">Parent page</p>
          <p className="truncate font-mono text-xs text-gray-700 dark:text-zinc-300" title={parentPageId}>
            {parentPageId}
          </p>
          <Link
            to={href}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            View parent page
            <ArrowUpRightIcon className="size-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
